/**
 * Descuentos operativos (faltas/permisos 0705, tardanzas 0704)
 * recalculados sobre sueldo_interno para boleta / planilla alternativa.
 */

import { montoProporcionalPorDias } from './ingresosDiasEspeciales';

export const CODIGOS_DESCUENTOS_OPERATIVOS = ['0704', '0705'];

export function esCodigoDescuentoOperativo(codigo) {
  return CODIGOS_DESCUENTOS_OPERATIVOS.includes(String(codigo || ''));
}

function idPlame(conceptosPlame, codigo, fallback) {
  const lista = Array.isArray(conceptosPlame)
    ? conceptosPlame
    : Object.values(conceptosPlame || {});
  const found = lista.find(
    (c) => String(c.codigo_plame || c.codigo) === String(codigo)
  );
  return found?.id ?? fallback ?? null;
}

function pushDescuento(lista, { codigo, concepto, id, monto }) {
  if (!(Number(monto) > 0)) return;
  lista.push({
    codigoPlame: codigo,
    codigo_plame: codigo,
    codigo,
    concepto,
    descripcion: concepto,
    id: id ?? null,
    monto: Number(monto),
    plame_configuracion_id: id ?? null,
    tipo_concepto: 'descuento',
    origenCalculo: 'descuentos_operativos_interna',
  });
}

export function contarFaltasPermisos(tareos = []) {
  const dias = { falta: 0, permiso: 0 };
  (tareos || []).forEach((tareo) => {
    const tipo = tareo.tipo_evento || tareo.tipoEvento;
    if (tipo === 'falta') dias.falta++;
    else if (tipo === 'permiso' || tipo === 'suspension') dias.permiso++;
  });
  return dias;
}

/** Horas de descanso semanal a descontar (misma regla PLAME: 8h − proporcionado). */
export function horasDescuentoDescansoSemanal(descansosSemanales) {
  if (!descansosSemanales?.configuracionActivada) return 0;
  const semanas = Array.isArray(descansosSemanales.semanasCalculadas)
    ? descansosSemanales.semanasCalculadas
    : [];
  const descansoCompletoSemana = 8;
  let totalHoras = 0;
  semanas.forEach((semana) => {
    if (semana.fechaInicio === semana.fechaFin) return;
    const proporcionado = Number(semana.descansoProporcionado) || 0;
    totalHoras += descansoCompletoSemana - proporcionado;
  });
  return totalHoras > 0 ? totalHoras : 0;
}

function montoPorHoras(base, horas, analisisPeriodo) {
  const b = Number(base) || 0;
  const h = Number(horas) || 0;
  if (b <= 0 || h <= 0 || !analisisPeriodo) return 0;

  let diasDelMes = 30;
  if (analisisPeriodo.tipo === 'mes_unico') {
    diasDelMes = Number(analisisPeriodo.mes?.diasCompletosDelMes) || 30;
  } else if (analisisPeriodo.tipo === 'dos_meses') {
    const d1 = Number(analisisPeriodo.primerMes?.diasCompletosDelMes) || 0;
    const d2 = Number(analisisPeriodo.segundoMes?.diasCompletosDelMes) || 0;
    diasDelMes = Math.max(d1, d2, 30);
  }
  if (diasDelMes <= 0) return 0;
  const montoPorHora = b / diasDelMes / 8;
  return parseFloat((montoPorHora * h).toFixed(2));
}

/**
 * @param {object} opts
 * @param {number} opts.sueldoBase - sueldo_interno (completo)
 * @param {Array} opts.tareos
 * @param {object} opts.analisisPeriodo
 * @param {object} [opts.descansosSemanales]
 * @param {number} [opts.minutosDeficit] - negativo = déficit (tardanzas); si >= 0 no hay 0704
 * @param {number} [opts.promedioHorasTeoricasDiarias=8]
 * @param {Array|object} [opts.conceptosPlame]
 */
export function calcularDescuentosOperativosInterna({
  sueldoBase,
  tareos = [],
  analisisPeriodo,
  descansosSemanales = null,
  minutosDeficit = 0,
  promedioHorasTeoricasDiarias = 8,
  conceptosPlame = [],
} = {}) {
  const descuentos = [];
  const base = Number(sueldoBase) || 0;
  const dias = contarFaltasPermisos(tareos);
  const totalDiasFaltasPermisos = dias.falta + dias.permiso;

  if (base > 0 && analisisPeriodo && totalDiasFaltasPermisos > 0) {
    let monto0705 = montoProporcionalPorDias(base, totalDiasFaltasPermisos, analisisPeriodo);
    const horasDescanso = horasDescuentoDescansoSemanal(descansosSemanales);
    if (horasDescanso > 0) {
      monto0705 = parseFloat((monto0705 + montoPorHoras(base, horasDescanso, analisisPeriodo)).toFixed(2));
    }
    pushDescuento(descuentos, {
      codigo: '0705',
      concepto: 'INASISTENCIAS',
      id: idPlame(conceptosPlame, '0705', 293),
      monto: monto0705,
    });
  }

  const deficit = Number(minutosDeficit) || 0;
  if (base > 0 && analisisPeriodo && deficit < 0) {
    const horasDeficit = Math.abs(deficit) / 60;
    const horasDia = Number(promedioHorasTeoricasDiarias) > 0
      ? Number(promedioHorasTeoricasDiarias)
      : 8;

    let diasDelMes = 30;
    if (analisisPeriodo.tipo === 'mes_unico') {
      diasDelMes = Number(analisisPeriodo.mes?.diasCompletosDelMes) || 30;
    } else if (analisisPeriodo.tipo === 'dos_meses') {
      const d1 = Number(analisisPeriodo.primerMes?.diasCompletosDelMes) || 0;
      const d2 = Number(analisisPeriodo.segundoMes?.diasCompletosDelMes) || 0;
      diasDelMes = Math.max(d1, d2, 30);
    }
    const valorHora = base / diasDelMes / horasDia;
    const monto0704 = parseFloat((valorHora * horasDeficit).toFixed(2));
    pushDescuento(descuentos, {
      codigo: '0704',
      concepto: 'TARDANZAS',
      id: idPlame(conceptosPlame, '0704', 292),
      monto: monto0704,
    });
  }

  return {
    descuentos,
    diasFaltasPermisos: dias,
    totalDiasFaltasPermisos,
  };
}

export default calcularDescuentosOperativosInterna;
